/**
 * The Morning Dispatch: at 06:00 of each in-game day (from day 2) the nation's paper lands on the
 * map table with the news since the last edition (game/newspaper.ts composes it from the match
 * chronicle). Files itself after 8 s (paused while hovered), click to put it away; a headline
 * with a place focuses the map there. Toggle in Settings.
 *
 * At high game speeds a new edition is printed at most every 10 s of real time; the next one then
 * covers all the days since the last paper — nothing is lost, and the table is not buried.
 */
import { dayOf } from '@friction/engine';
import { useEffect, useRef, useState } from 'react';
import { settingsStore } from '../../app/settings';
import { bus } from '../../game/bus';
import { chronicleStore, eventsBetween } from '../../game/chronicle';
import {
  composeNewspaper,
  crossedEdition,
  type NewsItem,
  type Newspaper as Paper,
} from '../../game/newspaper';
import { gameStore, useGame } from '../../game/store';
import { inEveryLocale, type Localized, useT } from '../../i18n';
import { tutorialStore } from '../../tutorial/store';
import { cx } from '../common/controls';
import s from './newspaper.module.css';

const LIFETIME_MS = 8000;
const MIN_GAP_MS = 10_000;

function Item({ it, lead }: { it: NewsItem; lead?: boolean }) {
  return (
    <article className={cx(lead ? s.lead : s.item, it.tone === 'bad' && s.bad)} data-hex={it.hex}>
      <div className={s.kicker}>{it.kicker}</div>
      {lead ? <h2 className={s.headline}>{it.head}</h2> : <h3 className={s.subhead}>{it.head}</h3>}
      <p className={lead ? s.lede : s.text}>{it.text}</p>
    </article>
  );
}

export function Newspaper() {
  const t = useT();
  // Printed in every language at once: switching the language swaps the whole edition.
  const [edition, setEdition] = useState<{ tick: number; paper: Localized<Paper> } | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [hover, setHover] = useState(false);
  const since = useRef(0);
  const lastShown = useRef(Number.NEGATIVE_INFINITY);
  const over = useGame((g) => g.view?.winners != null);

  useEffect(
    () =>
      gameStore.subscribe((st, prev) => {
        if (!st.view || !prev.view || !st.map || !st.grid || st.map !== prev.map) return;
        const tick = st.view.tick;
        if (!crossedEdition(prev.view.tick, tick) || dayOf(tick) < 2 || st.view.winners) return;
        if (!settingsStore.getState().newspaper || tutorialStore.getState().active) return;
        const now = performance.now();
        if (now - lastShown.current < MIN_GAP_MS) return;
        const { events } = chronicleStore.getState();
        const input = {
          events: eventsBetween(events, since.current, tick),
          history: events,
          view: st.view,
          map: st.map,
          grid: st.grid,
          day: dayOf(tick),
        };
        const paper = inEveryLocale((l) => composeNewspaper(input, l));
        since.current = tick;
        lastShown.current = now;
        setEdition({ tick, paper });
        setLeaving(false);
        setHover(false);
        bus.emit('sound', { name: 'paper' });
      }),
    [],
  );

  useEffect(() => {
    if (!edition || hover || leaving) return;
    const t = window.setTimeout(() => setLeaving(true), LIFETIME_MS);
    return () => clearTimeout(t);
  }, [edition, hover, leaving]);

  useEffect(() => {
    if (!leaving) return;
    const t = window.setTimeout(() => setEdition(null), 240);
    return () => clearTimeout(t);
  }, [leaving]);

  if (!edition || over) return null;
  const paper = edition.paper[t.locale];
  return (
    <div
      key={edition.tick}
      className={cx(s.paper, leaving && s.out)}
      role="dialog"
      aria-label={t('paper.aria', { masthead: paper.masthead, n: paper.day })}
      title={t('paper.putAway')}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        const hex = Number((e.target as HTMLElement).closest('[data-hex]')?.getAttribute('data-hex') ?? -1);
        if (hex >= 0) bus.emit('focusHex', { hex });
        bus.emit('sound', { name: 'paper', volume: 0.7 });
        setLeaving(true);
      }}
    >
      <div className={s.top}>
        <span>{paper.motto}</span>
        <span>{t('paper.edition')}</span>
      </div>
      <div className={s.masthead}>{paper.masthead}</div>
      <div className={s.dateline}>
        <span>{t('paper.no', { n: paper.day })}</span>
        <span>{t('paper.dateline', { n: paper.day })}</span>
        <span>{t('paper.price')}</span>
      </div>
      <Item it={paper.headline} lead />
      <div className={s.cols} style={{ gridTemplateColumns: `repeat(${paper.items.length}, 1fr)` }}>
        {paper.items.map((it) => (
          <Item key={it.head} it={it} />
        ))}
      </div>
    </div>
  );
}
